import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Command, ShieldAlert, Server,
  Building2, Users, Plus, PlusCircle, LayoutDashboard,
  Shield, AlertTriangle, Loader2, Radar
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/config/routes';
import { assetService } from '@/services/assetService';
import { vulnerabilityService } from '@/services/vulnerabilityService';
import { incidentService } from '@/services/nirpService';
import { organizationsService } from '@/services/organizationsService';
import { usersService } from '@/services/usersService';
import { threatIntelService } from '@/services/nirpService';
import { useAuthStore } from '@/store/authStore';
import { toEntitySlug } from '@/utils/slug';

interface CommandItem {
  id: string;
  name: string;
  group: string;
  icon: React.ElementType;
  href: string;
  /** Small caption under the name; defaults to the group label. */
  caption?: string;
  shortcut?: string[];
}

const COMMANDS: CommandItem[] = [
  /* Navigation */
  { id: 'dashboard', name: 'Go to Dashboard', group: 'Navigation', icon: LayoutDashboard, href: ROUTES.DASHBOARD },
  { id: 'assets', name: 'Go to Asset Management', group: 'Navigation', icon: Server, href: ROUTES.ASSETS },
  { id: 'vulnerabilities', name: 'Go to Vulnerabilities', group: 'Navigation', icon: AlertTriangle, href: ROUTES.VULNERABILITIES },
  { id: 'incidents', name: 'Go to Incident Management ', group: 'Navigation', icon: ShieldAlert, href: ROUTES.NIRP_ORG_DASHBOARD },
  { id: 'team', name: 'Go to Team Management', group: 'Navigation', icon: Users, href: ROUTES.TEAM },
  { id: 'admin-orgs', name: 'Go to Administration', group: 'Navigation', icon: Shield, href: ROUTES.ADMIN_ORGANIZATIONS },

  /* Quick Actions */
  { id: 'add-incident', name: 'Report New Incident', group: 'Actions', icon: PlusCircle, href: ROUTES.NIRP_SUBMIT_INCIDENT },
  { id: 'add-asset', name: 'Add New Asset', group: 'Actions', icon: Plus, href: ROUTES.CREATE_ASSET },
  { id: 'upload', name: 'Upload Data (CSV/Excel)', group: 'Actions', icon: Building2, href: ROUTES.NIRP_UPLOAD },
];

const GROUP_LABELS: Record<string, string> = {
  Actions: 'Quick Actions',
  Navigation: 'Platform Navigation',
  Assets: 'Assets',
  Vulnerabilities: 'Vulnerabilities',
  Incidents: 'Incidents',
  ThreatIntel: 'Threat Intel',
  Organizations: 'Organizations',
  Users: 'Users',
};

const GROUP_ORDER = [
  'Actions', 'Navigation', 'Assets', 'Vulnerabilities',
  'Incidents', 'ThreatIntel', 'Organizations', 'Users',
];

const MIN_QUERY_LENGTH = 2;
const RESULTS_PER_SOURCE = 5;

export function CommandPalette({ open, setOpen }: { open: boolean, setOpen: (open: boolean) => void }) {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [entityResults, setEntityResults] = React.useState<CommandItem[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);

  const commandMatches = React.useMemo(() => {
    return COMMANDS.filter(cmd =>
      cmd.name.toLowerCase().includes(search.toLowerCase()) ||
      cmd.group.toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  // Live entity search across assets, software, vulnerabilities, incidents,
  // threat intel, and (for platform admins) organizations and users.
  // Debounced so we don't hit every service on each keystroke.
  React.useEffect(() => {
    const query = search.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setEntityResults([]);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    const timer = setTimeout(async () => {
      const isPlatformAdmin = Boolean(
        useAuthStore.getState().user?.roles?.includes('platform_admin'),
      );
      const incidentsHref = isPlatformAdmin ? ROUTES.NIRP_ADMIN_INCIDENTS : ROUTES.NIRP_MY_INCIDENTS;

      const [assets, vulnerabilities, incidents, threats, orgs, users] = await Promise.all([
        assetService
          .list({ page: 1, pageSize: RESULTS_PER_SOURCE, search: query })
          .then((res) => res.data)
          .catch(() => []),
        vulnerabilityService
          .list({ page: 1, pageSize: RESULTS_PER_SOURCE, search: query })
          .then((res: any) => (res.data ?? []).slice(0, RESULTS_PER_SOURCE))
          .catch(() => []),
        incidentService
          .list({ page_size: RESULTS_PER_SOURCE, search: query })
          .then((res: any) => (res.results ?? res.data ?? []).slice(0, RESULTS_PER_SOURCE))
          .catch(() => []),
        threatIntelService
          .listDistributions({ page_size: RESULTS_PER_SOURCE, search: query })
          .then((res: any) => (res.results ?? res.data ?? []).slice(0, RESULTS_PER_SOURCE))
          .catch(() => []),
        isPlatformAdmin
          ? organizationsService
              .listAll({ page: 1, pageSize: RESULTS_PER_SOURCE, search: query })
              .then((res) => res.data)
              .catch(() => [])
          : Promise.resolve([]),
        isPlatformAdmin
          ? usersService
              .listAll({ page: 1, pageSize: RESULTS_PER_SOURCE, search: query })
              .then((res) => res.data)
              .catch(() => [])
          : Promise.resolve([]),
      ]);

      if (cancelled) return;

      setEntityResults([
        ...assets.map((asset: any): CommandItem => ({
          id: `asset:${asset.id}`,
          name: asset.name || asset.hostname || 'Asset',
          caption: [asset.asset_type, asset.ip_address].filter(Boolean).join(' · ') || 'Asset',
          group: 'Assets',
          icon: Server,
          href: `/assets/${asset.id}`,
        })),
        ...vulnerabilities.map((vuln: any): CommandItem => ({
          id: `vulnerability:${vuln.id}`,
          name: vuln.cve_id || 'Vulnerability',
          caption: [vuln.severity, vuln.affected_software].filter(Boolean).join(' · ') || 'Vulnerability',
          group: 'Vulnerabilities',
          icon: AlertTriangle,
          href: ROUTES.VULNERABILITIES,
        })),
        ...incidents.map((inc: any): CommandItem => ({
          id: `incident:${inc.id}`,
          name: inc.title || inc.incident_id || 'Incident',
          caption: [inc.incident_id, inc.severity, inc.status].filter(Boolean).join(' · '),
          group: 'Incidents',
          icon: ShieldAlert,
          href: incidentsHref,
        })),
        ...threats.map((intel: any): CommandItem => ({
          id: `threat:${intel.id}`,
          name: intel.title || 'Threat Intel',
          caption: intel.description
            ? String(intel.description).slice(0, 60)
            : 'Threat Intel',
          group: 'ThreatIntel',
          icon: Radar,
          href: ROUTES.NIRP_THREAT_DASHBOARD,
        })),
        ...orgs.map((org): CommandItem => ({
          id: `org:${org.id}`,
          name: org.name,
          caption: [org.sector, org.status].filter(Boolean).join(' · ') || 'Organization',
          group: 'Organizations',
          icon: Building2,
          href: `${ROUTES.ADMIN_ORGANIZATIONS}/${toEntitySlug(org.slug || org.name, org.id)}`,
        })),
        ...users.map((user): CommandItem => ({
          id: `user:${user.id}`,
          name: user.fullName || user.username || user.email,
          caption: [user.email, user.role].filter(Boolean).join(' · ') || 'User',
          group: 'Users',
          icon: Users,
          href: ROUTES.ADMIN_USERS,
        })),
      ]);
      setIsSearching(false);
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  const filteredItems = React.useMemo(
    () => [...commandMatches, ...entityResults],
    [commandMatches, entityResults],
  );

  // Reset selection when search changes
  React.useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  const onSelect = (href: string) => {
    navigate(href);
    setOpen(false);
    setSearch('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filteredItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filteredItems.length) % filteredItems.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredItems[selectedIndex]) {
        onSelect(filteredItems[selectedIndex].href);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden border-border shadow-xl animate-in zoom-in-95 duration-200">
        <DialogHeader className="p-4 border-b border-border/40 bg-muted/20">
          <div className="flex items-center gap-3">
            <Search className="h-5 w-5 text-primary" />
            <input
              autoFocus
              placeholder="Search assets, software, vulnerabilities, incidents, threats, organizations, users..."
              className="flex-1 bg-transparent border-none outline-hidden text-base text-foreground placeholder:text-muted-foreground/60"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {isSearching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/60" />}
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-muted/60 border border-border/50 text-[10px] font-black uppercase tracking-tighter text-muted-foreground/80">
              <span className="text-xs">ESC</span>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[400px] overflow-y-auto p-2">
          {filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Command className="h-10 w-10 text-muted-foreground/20 mb-3" />
              <p className="text-sm font-semibold text-muted-foreground">
                {isSearching ? 'Searching...' : 'No matches found.'}
              </p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                Try an asset, software title, CVE, incident, or organization name.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {/* Group items by category */}
              {GROUP_ORDER.map(group => {
                const groupItems = filteredItems.filter(item => item.group === group);
                if (groupItems.length === 0) return null;

                return (
                  <div key={group} className="flex flex-col gap-1">
                    <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground/40 mt-1 first:mt-0">
                      {GROUP_LABELS[group] ?? group}
                    </div>
                    {groupItems.map((item) => {
                      const absoluteIndex = filteredItems.findIndex(fi => fi.id === item.id);
                      const isSelected = absoluteIndex === selectedIndex;
                      const Icon = item.icon;

                      return (
                        <button
                          key={item.id}
                          onClick={() => onSelect(item.href)}
                          onMouseEnter={() => setSelectedIndex(absoluteIndex)}
                          className={cn(
                            'group flex items-center justify-between gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left',
                            isSelected
                              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25 translate-x-1'
                              : 'hover:bg-muted/60 text-muted-foreground hover:text-foreground'
                          )}
                        >
                          <div className="flex items-center gap-4">
                            <div className={cn(
                              'flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-300',
                              isSelected ? 'bg-white/20' : 'bg-muted group-hover:bg-accent'
                            )}>
                              <Icon className={cn('h-5 w-5', isSelected && 'animate-pulse text-white')} />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-bold tracking-tight">{item.name}</span>
                              <span className={cn(
                                'text-[10px] font-medium uppercase tracking-widest',
                                isSelected ? 'text-white/60' : 'text-muted-foreground/40'
                              )}>
                                {item.caption ?? item.group}
                              </span>
                            </div>
                          </div>
                          {isSelected && (
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-black uppercase text-white/50 tracking-widest">Select</span>
                              <div className="h-5 w-5 rounded bg-white/20 flex items-center justify-center border border-white/20 shadow-xs">
                                <kbd className="text-[10px]">↵</kbd>
                              </div>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-border/40 bg-muted/20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 grayscale opacity-60">
              <div className="h-5 w-5 rounded bg-muted/80 flex items-center justify-center border border-border/50 text-[10px]">↑↓</div>
              <span className="text-[10px] font-black uppercase text-muted-foreground/60 tracking-wider">Navigate</span>
            </div>
            <div className="flex items-center gap-1.5 grayscale opacity-60">
              <div className="h-5 w-5 rounded bg-muted/80 flex items-center justify-center border border-border/50 text-[10px]">↵</div>
              <span className="text-[10px] font-black uppercase text-muted-foreground/60 tracking-wider">to select</span>
            </div>
          </div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/40">
            BLUEB PLATFORM
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
